import React from 'react';
import {
  FormControl,
  InputAdornment,
  ListSubheader,
  MenuItem,
  styled,
  useTheme,
  Autocomplete,
  OutlinedInput,
} from '@mui/material';
import { WBIcon, WBTypography } from '@admiin-com/ds-web';
import { useFirmId } from '../../../../../../../hooks/useClientWorkflow/useClientWorkflow';
import { t } from 'i18next';
import { useSearchResults } from './hooks/useSearchResults';
import { EntityItem } from './components/EntityItem';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../../../../../../navigation/paths';

export function SearchEntity() {
  const [inputValue, setInputValue] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  const { firmId } = useFirmId();
  const hasClients = !!firmId;
  const theme = useTheme();
  const { options, value, loading, onChange } = useSearchResults({
    query: inputValue,
  });

  const handleInputChange = (
    event: React.ChangeEvent<object>,
    newValue: string
  ) => {
    setInputValue(newValue);
  };

  const handleChange = (event: any, option: any) => {
    onChange(event, option);
    setOpen(false); // close dropdown on selection
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && inputValue.trim()) {
      event.preventDefault();
      setOpen(false);
      setInputValue('');
      navigate(
        `${PATHS.clients}?search=${encodeURIComponent(inputValue.trim())}`
      );
    }
  };

  return (
    <FormControl sx={{ minWidth: '500px' }}>
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={value}
        options={options}
        getOptionLabel={(opt) => opt.name}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        includeInputInList
        filterOptions={(x) => x}
        renderInput={(params) => (
          <OutlinedInput
            {...params}
            {...params.InputProps}
            onKeyDown={handleKeyDown}
            sx={{ flex: 1 }}
            startAdornment={
              <InputAdornment position="start">
                <WBIcon name="Search" size="small" />
              </InputAdornment>
            }
            placeholder={t('searchClient', { ns: 'clients' })}
          />
        )}
        renderOption={(props, option) => (
          <MenuItem {...props} key={props.key}>
            <EntityItem entity={option} onClick={() => setOpen(false)} />
          </MenuItem>
        )}
        noOptionsText={
          hasClients ? (
            <MenuItem
              sx={{
                mt: 2,
                px: 0,
                mx: 2,
                borderTop: `1px solid ${theme.palette.grey[300]}`,
              }}
            >
              <WBTypography
                sx={{ textDecoration: 'underline' }}
                color="primary"
                fontWeight="bold"
              >
                {t('newClient', { ns: 'dashboard' })}
              </WBTypography>
            </MenuItem>
          ) : (
            <WBTypography>
              {t('No results found', { ns: 'common' })}
            </WBTypography>
          )
        }
        loading={loading}
        loadingText={t('Searching...', { ns: 'common' })}
        clearOnEscape
        fullWidth
        autoComplete
        filterSelectedOptions
        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
      />
    </FormControl>
  );
}

export const WBListSubheader = styled(ListSubheader)(({ theme }) => ({
  backgroundColor: 'inherit',
  color: theme.palette.text.primary,
  ...theme.typography.h5,
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  marginRight: theme.spacing(2),
  marginLeft: theme.spacing(2),
  paddingLeft: 0,
  position: 'initial',
}));
